import { ASSIGNMENT_STATUSES } from '@hirebotiq-ops/core';
import { Link, createFileRoute } from '@tanstack/react-router';
import { AdminLayout } from '../components/AdminLayout.js';
import { DataCard } from '../components/DataCard.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Field } from '../components/ui/field.js';
import { Input } from '../components/ui/input.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/assignments/$id')({
  component: AssignmentDetail,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

function AssignmentDetail() {
  const { id } = Route.useParams();
  const utils = trpc.useUtils();
  const q = trpc.assignments.getById.useQuery({ assignmentId: id });
  const setStatus = trpc.assignments.setStatus.useMutation({
    onSuccess: () => utils.assignments.getById.invalidate({ assignmentId: id }),
  });
  const update = trpc.assignments.update.useMutation({
    onSuccess: () => utils.assignments.getById.invalidate({ assignmentId: id }),
  });

  if (q.isLoading) {
    return (
      <AdminLayout>
        <p className="text-[var(--muted-foreground)]">Lade …</p>
      </AdminLayout>
    );
  }
  if (q.error || !q.data) {
    return (
      <AdminLayout>
        <p className="text-[oklch(0.55_0.2_25)]">
          Fehler: {q.error?.message ?? 'Einsatz nicht gefunden'}
        </p>
      </AdminLayout>
    );
  }

  const a = q.data;
  const workerName =
    [a.workerFirstName, a.workerLastName].filter(Boolean).join(' ') || '(unbenannt)';

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <Link
            to="/admin/assignments"
            className="text-sm text-[var(--muted-foreground)] hover:text-foreground"
          >
            ← Einsätze
          </Link>
          <h1 className="text-3xl font-bold mt-2">
            {workerName} → {a.clientCompanyName ?? '(Kunde)'}
          </h1>
          <p className="text-[var(--muted-foreground)]">
            Status: <StatusBadge status={a.status} /> · zuletzt geändert{' '}
            {new Date(a.updatedAt).toLocaleString('de-DE')}
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
            <CardDescription>Lebenszyklus des Einsatzes</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {ASSIGNMENT_STATUSES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={s === a.status ? 'default' : 'outline'}
                disabled={s === a.status || setStatus.isPending}
                onClick={() => setStatus.mutate({ assignmentId: a.id, status: s })}
              >
                {s}
              </Button>
            ))}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <DataCard
            title="Einsatz"
            rows={[
              ['Worker', workerName],
              ['Kunde', a.clientCompanyName],
              ['Start', a.startDate],
              ['Ende', a.endDate],
              ['Einsatzort', a.worksiteAddress],
              ['Rolle', a.roleDescription],
            ]}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Stammdaten bearbeiten</CardTitle>
            <CardDescription>Zeitraum, Ort, Tätigkeit, Notizen (auditiert)</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                update.mutate({
                  assignmentId: a.id,
                  startDate: (fd.get('startDate') as string) || '',
                  endDate: (fd.get('endDate') as string) || '',
                  worksiteAddress: (fd.get('worksiteAddress') as string) || undefined,
                  roleDescription: (fd.get('roleDescription') as string) || undefined,
                  notes: (fd.get('notes') as string) || undefined,
                });
              }}
            >
              <Field label="Start" htmlFor="startDate">
                <Input id="startDate" name="startDate" type="date" defaultValue={a.startDate ?? ''} />
              </Field>
              <Field label="Ende" htmlFor="endDate">
                <Input id="endDate" name="endDate" type="date" defaultValue={a.endDate ?? ''} />
              </Field>
              <Field label="Einsatzort" htmlFor="worksiteAddress">
                <Input
                  id="worksiteAddress"
                  name="worksiteAddress"
                  defaultValue={a.worksiteAddress ?? ''}
                />
              </Field>
              <Field label="Rolle / Tätigkeit" htmlFor="roleDescription">
                <Input
                  id="roleDescription"
                  name="roleDescription"
                  defaultValue={a.roleDescription ?? ''}
                />
              </Field>
              <Field label="Notizen" htmlFor="notes">
                <Input id="notes" name="notes" defaultValue={a.notes ?? ''} />
              </Field>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={update.isPending}>
                  {update.isPending ? 'Speichere …' : 'Speichern'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
